import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import CartContext from '../context/CartContext';
import { API } from '../config/api';
import getImageUrl from '../utils/getImageUrl';
import { Container, Row, Col, Card, Form, InputGroup, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faStar, faShoppingCart, faFire, faCrown } from '@fortawesome/free-solid-svg-icons';

const MenuPage = () => {
  const location = useLocation();
  const [menuItems, setMenuItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { addToCart, deliveryZones } = useContext(CartContext);
  const [filteredItems, setFilteredItems] = useState(menuItems);
  const [searchTerm, setSearchTerm] = useState('');

  // Check for ?category= in the URL on first load
  const getInitialCategory = () => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('category');
    if (cat) {
      // Capitalize first letter and rest lowercase to match category names
      return cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase();
    }
    return 'All';
  };
  const [categoryFilter, setCategoryFilter] = useState(getInitialCategory());
  const [priceFilter, setPriceFilter] = useState('All');
  const [sortOption, setSortOption] = useState('Most Ordered');

  // Get unique categories
  const categories = ['All', ...new Set(menuItems.map(item => item.category))];

  // Filter and sort functions
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('category');
    if (cat) {
      const formattedCat = cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase();
      if (categoryFilter !== formattedCat) {
        setCategoryFilter(formattedCat);
      }
    } else {
      // If no category param, reset to All
      setCategoryFilter('All');
    }
    // eslint-disable-next-line
  }, [location.search]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
  const response = await fetch(`${API}/products`);
        const data = await response.json();
        setMenuItems(data);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, [isLoading]);

  useEffect(() => {
    let results = menuItems;

    // Apply search filter
    if (searchTerm) {
      results = results.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply category filter
    if (categoryFilter !== 'All') {
      results = results.filter(item => item.category === categoryFilter);
    }

    // Apply price filter
    if (priceFilter === 'Under ₦3000') {
      results = results.filter(item => item.price < 3000);
    } else if (priceFilter === '₦3000 - ₦5000') {
      results = results.filter(item => item.price >= 3000 && item.price <= 5000);
    } else if (priceFilter === 'Over ₦5000') {
      results = results.filter(item => item.price > 5000);
    }

    // Apply sorting
    if (sortOption === 'Newest') {
      results = [...results].sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded));
    } else if (sortOption === 'Most Ordered') {
      results = [...results].sort((a, b) => b.orders - a.orders);
    } else if (sortOption === 'Highest Rating') {
      results = [...results].sort((a, b) => b.rating - a.rating);
    } else if (sortOption === 'Price: Low to High') {
      results = [...results].sort((a, b) => a.price - b.price);
    } else if (sortOption === 'Price: High to Low') {
      results = [...results].sort((a, b) => b.price - a.price);
    }

    setFilteredItems(results);
  }, [searchTerm, categoryFilter, priceFilter, sortOption, menuItems]);

  return (
    <Container className="my-5">
      {/* Header and Search */}
      <div className="text-center mb-5">
        <h1>Our Menu</h1>
        <p className="lead">Delicious treats made with love</p>
      </div>

      {/* Search and Filter Bar */}
      <div className="mb-4 p-3 bg-light rounded">
        <Row>
          <Col md={6} className="mb-3 mb-md-0">
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search menu items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col md={6}>
            <div className="d-flex flex-wrap justify-content-md-end gap-2">
              <Form.Select 
                style={{ width: 'auto' }} 
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  // Remove ?category= from URL if switching to All, otherwise update it
                  if (e.target.value === 'All') {
                    window.history.replaceState({}, '', '/menu');
                  } else {
                    window.history.replaceState({}, '', `/menu?category=${encodeURIComponent(e.target.value.toLowerCase())}`);
                  }
                }}
              >
                <option value="All">All Categories</option>
                {categories.filter(cat => cat !== 'All').map((category, index) => (
                  <option key={index} value={category}>{category}</option>
                ))}
              </Form.Select>

              <Form.Select 
                style={{ width: 'auto' }} 
                value={priceFilter}
                onChange={(e) => setPriceFilter(e.target.value)}
              >
                <option value="All">All Prices</option>
                <option value="Under ₦3000">Under ₦3000</option>
                <option value="₦3000 - ₦5000">₦3000 - ₦5000</option>
                <option value="Over ₦5000">Over ₦5000</option>
              </Form.Select>

              <Form.Select 
                style={{ width: 'auto' }} 
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
              >
                <option value="Most Ordered">Most Ordered</option>
                <option value="Newest">Newest</option>
                <option value="Highest Rating">Highest Rating</option>
                <option value="Price: Low to High">Price: Low to High</option>
                <option value="Price: High to Low">Price: High to Low</option>
              </Form.Select>
            </div>
          </Col>
        </Row>
      </div>

      {/* Menu Items Grid */}
      <Row xs={1} sm={2} md={3} lg={4} className="g-4">
        {filteredItems.map((item) => (
          <Col key={item.id}>
            <Card className="h-100 shadow-sm position-relative">
              {/* Popular Badge - Top Left */}
              {item.orders > 100 && (
                <div className="position-absolute top-0 start-0 bg-danger text-white px-2 py-1 small">
                  <FontAwesomeIcon icon={faFire} className="me-1" /> Popular
                </div>
              )}

              {/* Featured Badge - Top Right */}
              {item.featured && (
                <div className="position-absolute top-0 end-0 bg-warning text-dark px-2 py-1 small">
                  <FontAwesomeIcon icon={faCrown} className="me-1" /> Featured
                </div>
              )}

              <Card.Img 
                variant="top" 
                src={getImageUrl(item.image)} 
                style={{ height: '180px', objectFit: 'cover' }} 
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/300x180?text=No+Image';
                }}
              />
              <Card.Body className="d-flex flex-column">
                <Card.Title>{item.name}</Card.Title>
                <Card.Subtitle className="mb-2 text-muted">{item.category}</Card.Subtitle>
                {deliveryZones && (
                  <div className="delivery-info">
                    <small className="text-muted">
                      Available for delivery in: {deliveryZones.map(zone => zone.name).join(', ')}
                    </small>
                  </div>
                )}
                <div className="mb-2">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon 
                      key={i}
                      icon={faStar}
                      className={i < Math.floor(item.rating) ? "text-warning" : "text-muted"}
                    />
                  ))}
                  <span className="ms-2 small">({item.rating})</span>
                </div>
                <div className="mt-auto d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-danger">₦{item.price.toLocaleString()}</h5>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => addToCart(item)}
                  >
                    <FontAwesomeIcon icon={faShoppingCart} className="me-1" /> Add
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {filteredItems.length === 0 && (
        <div className="text-center py-5">
          <h4>No items found matching your criteria</h4>
          <Button variant="outline-danger" onClick={() => {
            setSearchTerm('');
            setCategoryFilter('All');
            setPriceFilter('All');
          }}>
            Reset Filters
          </Button>
        </div>
      )}
    </Container>
  );
}

export default MenuPage;